import { useState } from 'react';
import { api } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';

type Thumbs = {
  account_id: string; enabled: boolean; daily_cap: number; per_run: number;
  has_token: boolean; token_readable: boolean; token_hint: string; can_store_secrets: boolean;
  made_today: number; with_image: number; waiting: number; last_error: string;
};

/**
 * Pictures for Market News headlines, drawn by Cloudflare Workers AI on its
 * free allowance. The token is stored sealed and never shown again, only its
 * last four characters.
 */
export function NewsThumbs({ onChanged }: { onChanged: () => void }) {
  const { data, error, reload } = useAsync((signal) =>
    api.get<{ thumbs: Thumbs }>('/market-news/thumbs', signal));
  const [account, setAccount] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [cap, setCap] = useState<string | null>(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const t = data?.thumbs;
  if (error) return <div className="err">{String((error as Error).message)}</div>;
  if (!t) return null;

  async function act(what: string, fn: () => Promise<string>) {
    setBusy(what); setNote('');
    try { setNote(await fn()); await reload(); onChanged(); } catch (e) { setNote((e as Error).message); }
    setBusy('');
  }

  const save = (extra: Record<string, unknown> = {}) => act('save', async () => {
    await api.put('/market-news/thumbs', {
      account_id: account ?? t.account_id, ...(token ? { token } : {}),
      ...(cap != null ? { daily_cap: Number(cap) } : {}), ...extra,
    });
    setToken(''); setAccount(null); setCap(null);
    return 'Saved.';
  });

  return (
    <div className="crd" style={{ padding: '16px 22px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Thumbnails <span className="tg" style={{ marginLeft: 8, background: t.enabled ? 'rgba(62,207,142,.14)' : undefined }}>{t.enabled ? 'on' : 'off'}</span></div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>
            {t.with_image} headlines have a picture · {t.made_today} drawn today (limit {t.daily_cap}) · {t.waiting} waiting
          </div>
        </div>
        <button className="b bo" type="button" onClick={() => setOpen(!open)}>{open ? 'Close' : 'Set up'}</button>
      </div>

      {open ? (
        <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
          <p className="hint" style={{ margin: 0, lineHeight: 1.6 }}>
            Each headline gets one small abstract illustration (no text, logos or real people), drawn by Cloudflare
            Workers AI. It uses your free daily allowance. To set it up: in Cloudflare open <b>My Profile → API Tokens →
            Create Token</b>, use the <b>Workers AI</b> template, and paste the token here. Your Account ID is the 32-character
            code in your Cloudflare dashboard address.
          </p>
          {!t.can_store_secrets ? <div className="err">The server has no TVX_SECRET set, so a token cannot be stored safely.</div> : null}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
            <div className="fld"><label className="lbl">Cloudflare Account ID</label>
              <input className="inp" value={account ?? t.account_id} onChange={(e) => setAccount(e.target.value)} placeholder="32 letters and numbers" /></div>
            <div className="fld"><label className="lbl">API token {t.has_token ? `(saved ${t.token_hint})` : ''}</label>
              <input className="inp" type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)}
                placeholder={t.has_token ? 'Leave blank to keep the saved one' : 'Paste the Workers AI token'} /></div>
            <div className="fld"><label className="lbl">Pictures per day (max)</label>
              <input className="inp" type="number" min={1} max={500} value={cap ?? t.daily_cap} onChange={(e) => setCap(e.target.value)} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="b bg" type="button" disabled={busy !== ''} onClick={() => void save()}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            <button className="b bo" type="button" disabled={busy !== '' || !t.has_token}
              onClick={() => void act('test', async () => { const r = await api.post<{ bytes: number }>('/market-news/thumbs/test', {}); return `Works. A test picture was drawn (${Math.round(r.bytes / 1024)} KB), nothing was saved.`; })}>
              {busy === 'test' ? 'Testing…' : 'Test the token'}
            </button>
            <button className="b bo" type="button" disabled={busy !== '' || !t.enabled}
              onClick={() => void act('run', async () => { const r = await api.post<{ report: { made?: number; failed?: number; skipped?: string } }>('/market-news/thumbs/run', {}); return r.report.skipped ? 'Switch it on first.' : `Drew ${r.report.made ?? 0} picture(s)${r.report.failed ? `, ${r.report.failed} failed` : ''}.`; })}>
              {busy === 'run' ? 'Drawing…' : 'Draw some now'}
            </button>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <button type="button" role="switch" aria-checked={t.enabled} aria-label="Draw thumbnails" className="sw"
                {...(t.enabled ? { 'data-on': '' } : {})} style={{ border: 0, padding: 0 }} disabled={busy !== ''}
                onClick={() => void save({ enabled: !t.enabled })} />
              {t.enabled ? 'On' : 'Off'}
            </span>
          </div>
          {note ? <div className="crd" style={{ padding: '8px 14px', fontSize: 12.5 }}>{note}</div> : null}
          {t.last_error ? <div className="hint">Last problem: {t.last_error}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
